/**
 * Analytics Portal V1 — config + data hooks (Phase 9).
 *
 * `useAnalyticsConfig` is the ONLY thing that may run while the portal is disabled: it is the
 * safe flag/capability endpoint that decides whether the Analytics surface exists at all. No
 * dashboard, entity or refresh request is ever issued unless the config says the portal is
 * enabled AND visible AND the viewer is not locked.
 */
import { useQuery } from "@tanstack/react-query";
import {
  getAnalyticsConfig, getEntityDashboard, getManagedEntities, getNamedVisitors, getSummary,
  normalizeAnalyticsError,
} from "@/lib/analyticsApi";

const NO_RETRY = { retry: 0, refetchOnWindowFocus: false };

export function useAnalyticsConfig() {
  const query = useQuery({
    queryKey: ["analytics", "config"],
    queryFn: ({ signal }) => getAnalyticsConfig({ signal }),
    staleTime: 5 * 60 * 1000,
    ...NO_RETRY,
  });
  const config = query.data || null;
  return {
    config,
    loading: query.isLoading,
    // The portal surface exists only when the server says so.
    portalActive: Boolean(config?.enabled && config?.visible),
    locked: Boolean(config?.locked),
    legacyEnabled: Boolean(config?.legacy_page_enabled),
  };
}

/** Entities the viewer may open analytics for. `enabled:false` keeps it silent while locked. */
export function useManagedEntities({ category, enabled }) {
  return useQuery({
    queryKey: ["analytics", "entities", category || "all"],
    queryFn: ({ signal }) => getManagedEntities({ category, signal }),
    enabled: Boolean(enabled),
    staleTime: 60 * 1000,
    ...NO_RETRY,
  });
}

/**
 * One dashboard payload. The query key carries every input, so switching tab, entity or range
 * starts a new request and the previous one is aborted and disregarded by React Query.
 */
export function useAnalyticsDashboard({ tab, entityId, category, range, enabled }) {
  const isSummary = tab === "summary";
  return useQuery({
    queryKey: ["analytics", "dashboard", tab, isSummary ? category || "all" : entityId,
      range?.preset || "custom", range?.start || "", range?.end || "",
      range?.compare === false ? "solo" : "compare"],
    queryFn: ({ signal }) => (isSummary
      ? getSummary({ category, range, signal })
      : getEntityDashboard({ tab, entityId, range, signal })),
    enabled: Boolean(enabled) && (isSummary || Boolean(entityId)),
    staleTime: 30 * 1000,
    ...NO_RETRY,
  });
}

/**
 * Named visitors (Phase 16). `enabled` is driven by the panel being open AND the CAPITAL
 * capability, so no identity request is ever issued speculatively. The key carries entity, range
 * and sort, so changing any of them discards the previous rows instead of showing them against
 * the wrong period. `gcTime: 0` keeps rows out of the cache the moment the panel unmounts.
 */
export function useNamedVisitors({ entityType, entityId, range, sort, page, filters, enabled }) {
  const filterKey = JSON.stringify(filters || {});
  const query = useQuery({
    queryKey: ["analytics", "visitors", entityType, entityId, sort || "recent", page || 1,
      range?.preset || "custom", range?.start || "", range?.end || "", filterKey],
    queryFn: ({ signal }) => getNamedVisitors({ entityType, entityId, range, sort, page, filters,
                                               signal }),
    enabled: Boolean(enabled) && Boolean(entityType) && Boolean(entityId),
    staleTime: 0,
    gcTime: 0,
    ...NO_RETRY,
  });
  return {
    data: query.data || null,
    isLoading: query.isLoading || query.isFetching,
    error: query.error ? normalizeAnalyticsError(query.error) : null,
    refetch: query.refetch,
  };
}
